import { memo, useEffect, useState } from "react";
import styled from "styled-components";
import { useAtom, useAtomValue } from 'jotai';
import { useTranslation } from "react-i18next";
import { FaPlus, FaXmark } from "react-icons/fa6";
import { PiCloudArrowUpBold } from "react-icons/pi";
import { UI_Header, UI_HeaderLeft, UI_HeaderRight, UI_HeaderRight_item } from "../PanelStyle";
import { showUploadTaskPanelAtom } from "../../stores/mapStore";
import { UploadTask_GetPage_RequestQuery, UploadTaskAPIs } from "~/common/typescript/apis/UploadTaskAPIs";
import { currentProjectAtom } from "~/stores/ProjectStore";
import UploadTaskItem from "./UploadTaskItem";
import { showPanel as showUploadTask_UploadPanel } from "../UploadTask_UploadPanel/UploadTask_UploadPanel";
import {
  activeUploadTasksCountAtom,
  uploadTaskListRefreshTrigger,
  uploadTasksAtom,
} from "~/stores/UploadTaskStore";
import { Empty, Spin, Tooltip, Badge } from "antd";
import { ReloadOutlined, SyncOutlined } from "@ant-design/icons";
import { Rnd } from 'react-rnd';

/**
 * Hiển thị danh sách các task tải lên và tiến trình xử lý
 */
const UploadTaskPanel = () => {
  const { t } = useTranslation();
  const [showUploadTaskPanel, setShowUploadTaskPanel] = useAtom(showUploadTaskPanelAtom);
  const currentProject = useAtomValue(currentProjectAtom);
  const [uploadTasks, setUploadTasks] = useAtom(uploadTasksAtom);
  const activeCount = useAtomValue(activeUploadTasksCountAtom);
  const refreshTrigger = useAtomValue(uploadTaskListRefreshTrigger);
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const request: UploadTask_GetPage_RequestQuery = {
        page: 1,
        pageSize: 15,
      };
      if (currentProject?.id) {
        request.project_id = currentProject.id;
      }
      const result = await UploadTaskAPIs.remote_getPageUploadTasks(request);
      if (result && result.results) {
        setUploadTasks(result.results);
      }
    } catch (err) {
      console.error("Failed to fetch upload tasks:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentProject?.id, refreshTrigger]);

  return (
    <Rnd
      default={{
        x: Math.max(10, window.innerWidth - 380),
        y: Math.max(10, window.innerHeight - 560),
        width: 360,
        height: 480,
      }}
      minWidth={300}
      minHeight={250}
      dragHandleClassName="draggable-header"
      cancel=".no-drag, .no-drag *, button, input, textarea, select, a"
      bounds="window"
      enableResizing={{
        bottom: true,
        bottomRight: true,
        right: true,
        top: false,
        topLeft: false,
        topRight: false,
        left: false,
        bottomLeft: false,
      }}
      style={{ zIndex: 1000 }}
    >
      <UI_Wrapper>
        <UI_Header className="draggable-header" style={{ cursor: 'move', userSelect: 'none' }}>
          <UI_HeaderLeft className="no-drag" style={{ cursor: 'default', display: 'flex', alignItems: 'center' }}>
            <PiCloudArrowUpBold style={{ color: '#2563eb', fontSize: '18px', marginRight: 6 }} />
            <span style={{ marginRight: 6 }}>Tiến trình tải lên</span>
            {activeCount > 0 ? (
              <Badge
                count={`Đang xử lý: ${activeCount}`}
                style={{ backgroundColor: '#1677ff', fontSize: '11px' }}
              />
            ) : (
              <Badge
                count={uploadTasks.length}
                style={{ backgroundColor: '#8c8c8c', fontSize: '11px' }}
              />
            )}
          </UI_HeaderLeft>
          <UI_HeaderRight className="no-drag" style={{ cursor: 'default' }}>
            <Tooltip title="Làm mới">
              <UI_HeaderRight_item onClick={fetchData}>
                {isLoading ? <SyncOutlined spin /> : <ReloadOutlined />}
              </UI_HeaderRight_item>
            </Tooltip>
            <Tooltip title="Tải lên tệp GIS mới">
              <UI_HeaderRight_item
                onClick={() => {
                  showUploadTask_UploadPanel();
                }}
              >
                <FaPlus />
              </UI_HeaderRight_item>
            </Tooltip>
            <Tooltip title="Đóng">
              <UI_HeaderRight_item
                onClick={() => {
                  setShowUploadTaskPanel(false);
                }}
              >
                <FaXmark />
              </UI_HeaderRight_item>
            </Tooltip>
          </UI_HeaderRight>
        </UI_Header>

        <UI_Body_Wrapper>
          {isLoading && uploadTasks.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <Spin tip="Đang tải danh sách tác vụ..." />
            </div>
          ) : uploadTasks.length === 0 ? (
            <div style={{ padding: "30px 16px" }}>
              <Empty
                description="Chưa có tác vụ tải lên nào trong dự án"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </div>
          ) : (
            uploadTasks.map((uploadTask, index) => {
              return (
                <UploadTaskItem
                  key={uploadTask.id || index}
                  uploadTask={uploadTask}
                  isInitialOpen={index === 0}
                />
              );
            })
          )}
        </UI_Body_Wrapper>
      </UI_Wrapper>
    </Rnd>
  );
};

export default memo(UploadTaskPanel);

const UI_Wrapper = styled.div`
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
  border: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const UI_Body_Wrapper = styled.div`
  flex: 1;
  position: relative;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px;
`;
